import { signInWithCustomToken } from 'firebase/auth'

import type { AuthExchangeRequest, AuthExchangeResponse } from '@study-decks/shared'

import { getFirebaseAuth } from './firebase.js'
import { challengeFromVerifier, generateState, generateVerifier } from './pkce.js'

const VERIFIER_KEY = 'sd.pkce.verifier'
const STATE_KEY = 'sd.pkce.state'
const RETURN_TO_KEY = 'sd.return_to'
const GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth'

export interface SignInOptions {
  returnTo?: string
}

function redirectUri(): string {
  return `${window.location.origin}/auth/callback`
}

export async function startGoogleSignIn(opts: SignInOptions = {}): Promise<void> {
  const verifier = generateVerifier()
  const challenge = await challengeFromVerifier(verifier)
  const state = generateState()

  sessionStorage.setItem(VERIFIER_KEY, verifier)
  sessionStorage.setItem(STATE_KEY, state)
  if (opts.returnTo) sessionStorage.setItem(RETURN_TO_KEY, opts.returnTo)

  const params = new URLSearchParams({
    client_id: import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID,
    redirect_uri: redirectUri(),
    response_type: 'code',
    scope: 'openid email profile',
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state,
    prompt: 'select_account',
    hd: import.meta.env.VITE_DOMAIN_HINT,
  })
  window.location.assign(`${GOOGLE_AUTH_URL}?${params.toString()}`)
}

export interface CompleteSignInResult {
  user: AuthExchangeResponse['user']
  returnTo: string
}

export async function completeGoogleSignIn(): Promise<CompleteSignInResult> {
  const url = new URL(window.location.href)
  const code = url.searchParams.get('code')
  const state = url.searchParams.get('state')
  const error = url.searchParams.get('error')

  if (error) throw new Error(`Google sign-in error: ${error}`)
  if (!code) throw new Error('Missing auth code')
  if (!state) throw new Error('Missing state')

  const expectedState = sessionStorage.getItem(STATE_KEY)
  const verifier = sessionStorage.getItem(VERIFIER_KEY)
  const returnTo = sessionStorage.getItem(RETURN_TO_KEY) ?? '/'

  sessionStorage.removeItem(STATE_KEY)
  sessionStorage.removeItem(VERIFIER_KEY)
  sessionStorage.removeItem(RETURN_TO_KEY)

  if (!expectedState || state !== expectedState) {
    throw new Error('State mismatch — possible CSRF; please sign in again')
  }
  if (!verifier) throw new Error('Missing PKCE verifier; please sign in again')

  const body: AuthExchangeRequest = {
    code,
    codeVerifier: verifier,
    redirectUri: redirectUri(),
  }
  const res = await fetch('/api/auth/exchange', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'include',
  })
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as {
      error?: { code?: string; message?: string; detail?: string }
    }
    const code = errBody.error?.code ?? 'EXCHANGE_FAILED'
    const message = errBody.error?.message ?? `Sign-in failed (${res.status})`
    const detail = errBody.error?.detail
    throw new SignInError(code, detail ? `${message}: ${detail}` : message)
  }
  const json = (await res.json()) as AuthExchangeResponse
  await signInWithCustomToken(getFirebaseAuth(), json.customToken)
  return { user: json.user, returnTo }
}

export class SignInError extends Error {
  override readonly name = 'SignInError'
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message)
  }
}

export async function postLogout(idToken: string): Promise<void> {
  await fetch('/api/auth/logout', {
    method: 'POST',
    headers: { authorization: `Bearer ${idToken}` },
    credentials: 'include',
  })
}
